import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { NotesFieldDE } from "@/shared/data-entries/common/NotesFieldDE";
import { ErrorModal } from "@dexteel/mesf-core";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { createSampleWizardTheme } from "@/setup/ag-grid/mesf-theme";
import { useEffect, useState, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { ChemistrySample } from "../../models/lab.models";
import { createSample } from "../../repositories/lab-de.repositories";
import { SampleStep } from "./SampleStep";

const steps = ["Sample Attributes", "Element Values", "Comments"] as const;

interface CreateSampleWizardProps {
  show: boolean;
  onHide: (onHide: boolean) => void;
  initialData?: ChemistrySample;
  columns: ColDef[];
  existingSamples?: { sampleTypeName: string; sampleSequence?: number }[];
  heatId: number | null;
  refetch?: () => void;
}

export const CreateSampleWizard: React.FC<CreateSampleWizardProps> = ({
  show,
  onHide,
  initialData,
  columns,
  existingSamples = [],
  heatId,
  refetch = () => {},
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [gridValidationError, setGridValidationError] = useState<string | null>(
    null,
  );
  const [notificationMessage, setNotificationMessage] = useState<string>("");

  const createEmptyElementValues = () => {
    const emptyValues: Record<string, number | null> = {};
    columns.forEach((col) => {
      if (col.field) {
        emptyValues[col.field] = null;
      }
    });
    return emptyValues;
  };

  const [singleRowData, setSingleRowData] = useState<
    Record<string, number | null>
  >(initialData?.ElementValues || createEmptyElementValues());

  const gridRef = useRef<AgGridReact>(null);

  const { handleSubmit, control, reset, setValue } = useForm<ChemistrySample>({
    defaultValues: initialData || {
      SampleTypeId: null,
      SampleTypeName: "",
      Reported: new Date(),
      SampleSequence: null,
      Technician: "",
      Comments: "",
      ElementValues: createEmptyElementValues(),
    },
  });

  const validateGridData = (): boolean => {
    const rowData = gridRef.current?.api?.getDisplayedRowAtIndex(0)?.data;
    const requiredFields = ["C", "Mn", "S", "P", "Si"];
    const areRequiredFieldsFilled =
      rowData &&
      requiredFields.every((field) => {
        return (
          rowData.hasOwnProperty(field) &&
          rowData[field] !== null &&
          rowData[field] !== undefined &&
          rowData[field] !== ""
        );
      });

    if (!areRequiredFieldsFilled) {
      setGridValidationError(
        "Please fill in the required fields: C, Mn, S, P, Si.",
      );
    } else {
      setGridValidationError(null);
    }

    return areRequiredFieldsFilled;
  };

  useEffect(() => {
    if (show && initialData) {
      reset(initialData);
      setSingleRowData(initialData.ElementValues || createEmptyElementValues());
      setActiveStep(0);
    } else if (show) {
      reset({
        SampleTypeId: null,
        SampleTypeName: "",
        Reported: new Date(),
        SampleSequence: null,
        Technician: "",
        Comments: "",
        ElementValues: createEmptyElementValues(),
      });
      setSingleRowData(createEmptyElementValues());
      setActiveStep(0);
    }
  }, [show, initialData, reset]);

  const defaultColDef = {
    flex: 1,
    minWidth: 80,
    resizable: false,
    sortable: false,
    filter: false,
    cellStyle: (params: any) => {
      const isEmpty =
        params.value === null ||
        params.value === undefined ||
        params.value === "";
      const style: Record<string, string | number> = {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        boxSizing: "border-box",
      };
      if (isEmpty) {
        style.border = "2px solid #f44336";
        style.borderRadius = "4px";
      }
      return style;
    },
  };

  const handleNext = () => {
    if (activeStep === 1) {
      const isValid = validateGridData();
      if (!isValid) return;

      const gridData =
        gridRef.current?.api?.getDisplayedRowAtIndex(0)?.data || singleRowData;
      setValue("ElementValues", gridData);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => {
      if (prevActiveStep === 1) {
        setGridValidationError(null);
      }
      return prevActiveStep - 1;
    });
  };

  const handleComplete = async (data: ChemistrySample) => {
    if (activeStep === 1) {
      const gridData =
        gridRef.current?.api?.getDisplayedRowAtIndex(0)?.data || singleRowData;
      data.ElementValues = gridData;
    }

    setIsSubmitting(true);
    try {
      const resp = await createSample(heatId, data);
      if (!resp.ok) {
        setError(resp.message || "Failed to save data");
        return;
      }
      setNotificationMessage("Sample created successfully");
      onHide(true);
      refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error saving data");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onHide(false);
  };

  return (
    <>
      <Dialog fullWidth maxWidth="md" open={show} onClose={handleCancel}>
        <DialogTitle sx={{ padding: "5px 24px 10px" }}>
          <Typography
            variant="h5"
            sx={{
              margin: "10px 0",
              fontWeight: 600,
              userSelect: "none",
              marginLeft: "5px",
            }}
          >
            {activeStep === 0 && "New Sample - Attributes"}
            {activeStep === 1 && "New Sample - Elements"}
            {activeStep === 2 && "New Sample - Comments"}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ padding: "10px 24px" }} dividers>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{ background: "none" }}
            connector={
              <StepConnector
                sx={{
                  "&.MuiStepConnector-alternativeLabel": {
                    top: 22,
                  },
                  "&.Mui-active": {
                    "& .MuiStepConnector-line": {
                      backgroundColor: "#3f51b5",
                    },
                  },
                  "&.Mui-completed": {
                    "& .MuiStepConnector-line": {
                      backgroundColor: "#4caf50",
                    },
                  },
                  "& .MuiStepConnector-line": {
                    height: 3,
                    border: 0,
                    backgroundColor: "#eaeaf0",
                    borderRadius: 1,
                  },
                }}
              />
            }
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  slotProps={{
                    stepIcon: {
                      sx: {
                        color: (theme) => theme.palette.primary.main,
                        background: (theme) => theme.palette.background.paper,
                        "&.Mui-completed": {
                          color: (theme) => theme.palette.success.main,
                        },
                      },
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <form
            onSubmit={handleSubmit(handleComplete)}
            style={{ width: "100%" }}
          >
            {activeStep === 0 && (
              <SampleStep
                control={control}
                setValue={setValue}
                existingSamples={existingSamples}
              />
            )}

            {activeStep === 1 && (
              <Box
                sx={{
                  height: "82px",
                  width: "100%",
                  marginTop: (theme) => theme.spacing(2),
                  marginBottom: (theme) => theme.spacing(4),
                }}
              >
                <AgGridReact
                  ref={gridRef}
                  rowData={[singleRowData]}
                  rowHeight={30}
                  headerHeight={30}
                  columnDefs={columns}
                  defaultColDef={defaultColDef}
                  animateRows={true}
                  onCellValueChanged={(params) => {
                    setSingleRowData(params.data);
                  }}
                  suppressContextMenu={true}
                  singleClickEdit={true}
                  theme={createSampleWizardTheme}
                />

                <Box sx={{ minHeight: 20, margin: "4px 0" }}>
                  <Typography sx={{ fontSize: "0.8rem", color: "red" }}>
                    {gridValidationError ?? " "}
                  </Typography>
                </Box>
              </Box>
            )}

            {activeStep === 2 && (
              <Box mt={2}>
                <Controller
                  name="Comments"
                  control={control}
                  render={({ field }) => (
                    <NotesFieldDE
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      placeholder="Add your comments here..."
                      minRows={3}
                      maxRows={3}
                    />
                  )}
                />
              </Box>
            )}

            <DialogActions sx={{ margin: "15px 0", padding: "0 10px" }}>
              <Grid container spacing={1}>
                <Grid
                  item
                  xs={4}
                  sx={{ marginRight: "auto", padding: "8px 10px" }}
                >
                  <Button
                    fullWidth
                    sx={{
                      padding: "6px 16px",
                      fontSize: "0.875rem",
                      lineHeight: 1.75,
                      borderRadius: "4px",
                    }}
                    variant="contained"
                    color="inherit"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </Grid>

                {activeStep > 0 && (
                  <Grid item xs={4} sx={{ padding: "8px 10px" }}>
                    <Button
                      fullWidth
                      sx={{
                        padding: "6px 16px",
                        fontSize: "0.875rem",
                        lineHeight: 1.75,
                        borderRadius: "4px",
                      }}
                      variant="outlined"
                      color="secondary"
                      onClick={handleBack}
                      disabled={isSubmitting}
                    >
                      Back
                    </Button>
                  </Grid>
                )}

                <Grid item xs={4} sx={{ padding: "8px 10px" }}>
                  <Button
                    fullWidth
                    sx={{
                      padding: "6px 16px",
                      fontSize: "0.875rem",
                      lineHeight: 1.75,
                      borderRadius: "4px",
                    }}
                    variant="contained"
                    color="primary"
                    onClick={
                      activeStep === steps.length - 1
                        ? handleSubmit(handleComplete)
                        : handleSubmit(() => handleNext())
                    }
                    disabled={isSubmitting}
                    startIcon={
                      isSubmitting ? <CircularProgress size={24} /> : null
                    }
                  >
                    {activeStep === steps.length - 1 ? "Save" : "Next"}
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />

      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
